import MirrorBackButton from "@/components/MirrorBackButton";
import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence, MotionConfig, useReducedMotion } from "framer-motion";
import MobileLayout from "@/components/MobileLayout";
import {
  ChevronLeft, Settings, Moon, Sun, Zap, Heart, Star, CloudRain, Frown, Meh,
  ArrowRight, X, Bell, Clock, Target, TrendingUp, Watch, Activity,
  Circle, Lock, Mic, Trash2, Loader2,
} from "lucide-react";
import { useLanguage } from "@/i18n/LanguageProvider";
import { InfoTooltip } from "@/components/InfoTooltip";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useOneSignal } from "@/hooks/useOneSignal";
import SleepActionModal, { SleepPendingActionLauncher } from '@/components/mirror/SleepActionModal';
import { newSleepOperationId, snapshotForSleep, type SleepActionPrefill, type SleepRequest, type SleepResponse } from '@/lib/sleepExecutor';
import { useSleep } from "@/hooks/useSleep";
import type { SleepLog } from "@/hooks/useSleep";
import { ModuleHistoryList } from "@/components/history/ModuleHistoryList";
import { AnalyticsDashboard } from "@/components/analytics/AnalyticsDashboard";
import { buildDashboard } from "@/lib/analytics/dashboard";
import { SLEEP_DASHBOARD, extractSleepSamples } from "@/lib/analytics/sleepDashboard";
import type { Granularity } from "@/lib/analytics/engine";
import { AgendaBottomSheet } from "@/components/agenda/CreateEventModal";
import { History } from "lucide-react";
import { useXP } from "@/hooks/useXP";
import { useSleepReact } from "@/hooks/useSleepReact";
import { useVoiceInputWhisper } from "@/hooks/useVoiceInputWhisper";
import { SleepLogSheet } from "@/components/sleep/SleepLogSheet";
import WearableSources from "@/components/sleep/WearableSources";
import EstimatedScoreCard from "@/components/sleep/EstimatedScoreCard";
import MirrorSleepCard from "@/components/sleep/MirrorSleepCard";
import {
  HrvTrendCard,
  ScheduleConsistencyCard,
  SleepTrendSummary,
} from "@/components/sleep/SleepTrendCards";
import type { TrendNight } from "@/lib/sleep/trends";
import { sleepDurationValue, sleepScoreValue } from "@/lib/sleep/values";
import { useHealthMetrics } from "@/hooks/useHealthMetrics";
import { readinessScore, recoveryScore, toMetricPoints } from "@/lib/health/recovery";
import { SLEEP_FUTURE_METRICS } from "@/lib/health/futureMetrics";
import type { SleepQuality, SleepNote } from "@/hooks/useSleep";

const ACCENT = "#00e5ff";

// Ícone por métrica futura — decisão visual DESTA página; a lista em si vem de
// futureMetrics.ts (o pin wearableTruth a mantém disjunta de HEALTH_READ_TYPES).
const FUTURE_METRIC_ICON: Record<string, React.ElementType> = {
  "sleep.insights.spo2": Activity,
  "sleep.insights.respiratory": TrendingUp,
  "sleep.insights.temperature": Zap,
};

const QUALITY_OPTIONS: { value: SleepQuality; icon: React.ElementType; color: string; key: string }[] = [
  { value: "amazing", icon: Star, color: ACCENT, key: "sleep.quality.amazing" },
  { value: "good", icon: Sun, color: "#14b8a6", key: "sleep.quality.good" },
  { value: "fair", icon: Meh, color: "#6b7280", key: "sleep.quality.fair" },
  { value: "poor", icon: CloudRain, color: "#f59e0b", key: "sleep.quality.poor" },
  { value: "terrible", icon: Frown, color: "#ef4444", key: "sleep.quality.terrible" },
];

const NOTE_OPTIONS: { value: SleepNote; key: string }[] = [
  { value: "caffeine", key: "sleep.notes.caffeine" },
  { value: "alcohol", key: "sleep.notes.alcohol" },
  { value: "exercise", key: "sleep.notes.exercise" },
  { value: "stress", key: "sleep.notes.stress" },
  { value: "screen_time", key: "sleep.notes.screenTime" },
  { value: "late_meal", key: "sleep.notes.lateMeal" },
  { value: "medication", key: "sleep.notes.medication" },
  { value: "nap", key: "sleep.notes.nap" },
];

const QUALITY_COLORS: Record<SleepQuality, string> = {
  amazing: ACCENT, good: "#14b8a6", fair: "#6b7280", poor: "#f59e0b", terrible: "#ef4444",
};

function formatDuration(hours: number): string {
  const minutes = Math.round(hours * 60);
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}h ${m.toString().padStart(2, "0")}min`;
}

function generateSleepWave(width: number, height: number): string {
  const points = [
    [0, height * 0.5], [width * 0.08, height * 0.3], [width * 0.15, height * 0.1],
    [width * 0.22, height * 0.4], [width * 0.30, height * 0.6], [width * 0.38, height * 0.2],
    [width * 0.45, height * 0.05], [width * 0.52, height * 0.35], [width * 0.60, height * 0.65],
    [width * 0.68, height * 0.25], [width * 0.75, height * 0.08], [width * 0.82, height * 0.45],
    [width * 0.90, height * 0.70], [width * 0.96, height * 0.55], [width, height * 0.45],
  ];
  let d = `M ${points[0][0]} ${points[0][1]}`;
  for (let i = 1; i < points.length - 1; i++) {
    const cpx = (points[i][0] + points[i + 1][0]) / 2;
    const cpy = (points[i][1] + points[i + 1][1]) / 2;
    d += ` Q ${points[i][0]} ${points[i][1]} ${cpx} ${cpy}`;
  }
  d += ` L ${points[points.length - 1][0]} ${points[points.length - 1][1]}`;
  return d;
}

const NOTE_ICONS: Record<string, string> = {
  caffeine: "☕", alcohol: "🍷", exercise: "💪", stress: "😰",
  screen_time: "📱", late_meal: "🍽️", medication: "💊", nap: "😴",
};

const Sleep: React.FC = () => {
  const sleep = useSleep();
  const { t } = useLanguage();
  const navigate = useNavigate();
  if (!sleep.hasData) return (
    <MobileLayout className="pb-28 pt-4">
      <MirrorBackButton className="mb-6" />
      <h1 className="text-2xl font-display">{t("sleep.title")}</h1>
      <div role="status" className="py-10 text-center space-y-4">
        {sleep.status === "loading" ? <Loader2 className="animate-spin mx-auto" aria-label={t("common.loading")} /> : <>
          <p>{t(sleep.status === "signed-out" ? "sleep.signIn" : "sleep.loadError")}</p>
          <button disabled={sleep.isRefreshing} className="min-h-11 rounded-xl bg-primary px-5 text-primary-foreground" onClick={() => sleep.status === "signed-out" ? navigate("/auth?mode=signin", { state: { from: "/sleep" } }) : void sleep.refresh()}>
            {t(sleep.status === "signed-out" ? "sleep.enter" : "common.retry")}
          </button>
        </>}
      </div>
    </MobileLayout>
  );
  return <MotionConfig reducedMotion="user"><SleepContent key={sleep.userId + ":" + sleep.identityGeneration} sleep={sleep} /></MotionConfig>;
};

const SleepContent: React.FC<{ sleep: ReturnType<typeof useSleep> }> = ({ sleep }) => {
  const reducedMotion = useReducedMotion();
  const navigate = useNavigate();
  const { t, tf, lang } = useLanguage();
  const { userId, lastNightSleep, getSleepHistory, stats, targetHours, refresh,
    preferences, capture, isSaving, checkedAt, status } = sleep;
  const { requestPermission: requestPushPermission } = useOneSignal(userId);
  const confirmedActor = capture();
  const xp = useXP();
  const { totalXP, level, getLevelProgress } = xp;
  const [actionPrefill, setActionPrefill] = useState<SleepActionPrefill | null>(null);
  const settingsOriginal = useRef<typeof sleep.preferencesSnapshot>();
  const mirrorReact = useSleepReact();
  const voiceInput = useVoiceInputWhisper(lang);

  // Tab state
  const [activeTab, setActiveTab] = useState<"log" | "insights">("log");

  // 3-step sheet state (new empty-state flow).
  const [showSheet, setShowSheet] = useState(false);
  const [sheetTimes, setSheetTimes] = useState<{ bedtime: string; wakeup: string } | undefined>();

  // Tracks which sleep_log id we've already requested a Mirror reaction for
  // in this session — prevents a refetch storm when the hero re-renders.
  const reactedForRef = useRef<string | null>(null);

  // Hides the "Same as yesterday" button when no prior log exists. Null until
  // we've checked history.
  const [hasHistory, setHasHistory] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const history = await getSleepHistory(7);
        if (!cancelled) setHasHistory(history.some(log => log.id !== lastNightSleep?.id));
      } catch {
        if (!cancelled) setHasHistory(false);
      }
    })();
    return () => { cancelled = true; };
  }, [getSleepHistory, checkedAt, lastNightSleep?.id]);

  // Fire Mirror reaction once when a fresh/load-existing lastNightSleep is
  // available. Covers both "just saved via sheet" and "page refresh with
  // existing log" paths. Skips the temp id that useSleep optimistically
  // assigns before the DB roundtrip resolves.
  useEffect(() => {
    const id = lastNightSleep?.id;
    if (!id || id === "temp") return;
    if (reactedForRef.current === id) return;
    if (mirrorReact.isLoading) return;
    reactedForRef.current = id;
    // Pass the canonical app language code ("PT-BR" | "EN" | "NL") so the
    // sleep-react edge function selects the correct localized prompt.
    mirrorReact.react({ sleepLogId: id, language: lang });
  }, [lastNightSleep?.id, lang, mirrorReact]);

  // Append transcribed speech to the notes field once per recording cycle.
  // The Whisper hook keeps `transcript` hot across renders, so we track the
  // last value we've consumed to avoid double-appending.
  const consumedTranscriptRef = useRef("");
  useEffect(() => {
    if (voiceInput.isListening || voiceInput.isTranscribing) return;
    const tr = voiceInput.transcript.trim();
    if (!tr || tr === consumedTranscriptRef.current) return;
    consumedTranscriptRef.current = tr;
    setNoteText((prev) => (prev ? `${prev} ${tr}` : tr));
  }, [voiceInput.isListening, voiceInput.isTranscribing, voiceInput.transcript]);

  // Log form state
  const [bedtime, setBedtime] = useState("");
  const [wakeup, setWakeup] = useState("");
  const [quality, setQuality] = useState<SleepQuality | null>(null);
  const [notes, setNotes] = useState<SleepNote[]>([]);
  const [noteText, setNoteText] = useState("");
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const editNightRef = useRef<SleepLog | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Weekly chart state
  const [activeDay, setActiveDay] = useState(6);

  // Settings modal
  const [showSettings, setShowSettings] = useState(false);
  const [settingsTarget, setSettingsTarget] = useState("8");
  const [smartAlarm, setSmartAlarm] = useState(false);
  const [reminderTime, setReminderTime] = useState("22:30");
  const [wakeupReminder, setWakeupReminder] = useState(false);
  const [wakeupReminderTime, setWakeupReminderTime] = useState("06:30");

  // Lock body scroll when settings open
  useEffect(() => {
    if (showSettings) { document.body.style.overflow = "hidden"; } else { document.body.style.overflow = ""; }
    return () => { document.body.style.overflow = ""; };
  }, [showSettings]);

  // Drafts come from the confirmed account preferences on each opening.
  useEffect(() => {
    if (!showSettings) return;
    settingsOriginal.current = sleep.preferencesSnapshot === undefined ? undefined : structuredClone(sleep.preferencesSnapshot);
    setSettingsTarget(preferences?.sleep_target_hours || "8");
    setSmartAlarm(preferences?.sleep_bedtime_reminder ?? false);
    setReminderTime(preferences?.sleep_bedtime_reminder_time || "22:30");
    setWakeupReminder(preferences?.sleep_wakeup_reminder ?? false);
    setWakeupReminderTime(preferences?.sleep_wakeup_reminder_time || "06:30");
  // The settings draft keeps the original shown state across background refreshes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSettings]);

  // Pre-populate form when editing
  useEffect(() => {
    if (lastNightSleep && editing) {
      editNightRef.current = lastNightSleep;
      setBedtime(lastNightSleep.bedtime.slice(0, 5));
      setWakeup(lastNightSleep.wakeup.slice(0, 5));
      setQuality(lastNightSleep.quality || null);
      setNotes((lastNightSleep.notes as SleepNote[]) || []);
      setNoteText(lastNightSleep.note_text || "");
    }
  }, [editing]);

  const showReceiptRewards = async (response: SleepResponse, current: () => boolean) => {
    const proof = response.xp;
    if (!proof || proof.user_id !== userId || !current()) return;
    for (const award of proof.awards) if (award.inserted && award.amount > 0) toast.success('+' + award.amount + ' XP');
    try { await xp.refresh({ ownerId: userId, isCurrent: current }); if (current() && proof.leveled_up) toast.success(t('habits.xp.levelUp') + ' ' + proof.level); } catch { /* Original atomic XP remains confirmed. */ }
  };
  const openAction = (prefill: Omit<SleepActionPrefill, 'action_id'|'operation_id'|'expected_user_id'>) => {
    if (!userId || isSaving) return; const operation_id = newSleepOperationId();
    setActionPrefill({ ...prefill, action_id: operation_id, operation_id, expected_user_id: userId });
  };
  const editPatch = (log: SleepLog, payload: { bedtime:string;wakeup:string;quality:SleepQuality;notes?:SleepNote[];note_text?:string }) => Object.fromEntries(Object.entries(payload).filter(([key,value]) => {
    if(key==='bedtime'||key==='wakeup')return value!==String(log[key]).slice(0,5);
    if(key==='notes')return JSON.stringify(value)!==JSON.stringify(log.notes??[]);
    if(key==='note_text')return value!==(log.note_text??'');
    return value!==(log as unknown as Record<string,unknown>)[key];
  }));
  const handleSave = async () => {
    if (saving || isSaving || !bedtime || !wakeup || !quality) return;
    const original = editing ? editNightRef.current : lastNightSleep;
    if (original) { const snapshot = snapshotForSleep(original, userId??undefined); if(!snapshot){toast.error(t('sleepAction.readOnly'));return;} openAction({ action:'update',sleep_id:original.id,expected_snapshot:snapshot,fields:editPatch(original,{bedtime,wakeup,quality,notes,note_text:noteText}) }); }
    else openAction({ action:'create', sleep_date:sleep.sleepDay,fields:{bedtime,wakeup,quality,notes,note_text:noteText||null} });
  };
  // Reuse a real previous night's times, then review them and select quality.
  const handleQuickLog = async () => {
    const current = capture();
    try {
      const history = await getSleepHistory(7);
      if (!current()) return;
      const recent = history.find(log => log.id !== lastNightSleep?.id);
      if (!recent) return;
      setSheetTimes({ bedtime: recent.bedtime.slice(0, 5), wakeup: recent.wakeup.slice(0, 5) });
      setShowSheet(true);
    } catch (error) {
      console.error("[Sleep] previous night failed", error);
      if (current()) toast.error(t("sleep.loadError"));
    }
  };
  const handleSheetSave = async (payload: { bedtime:string;wakeup:string;quality:SleepQuality }) => {
    const original=lastNightSleep; setShowSheet(false);
    if(original){const snapshot=snapshotForSleep(original,userId??undefined);if(!snapshot)throw new Error('sleep_read_only');openAction({action:'update',sleep_id:original.id,expected_snapshot:snapshot,fields:editPatch(original,payload)});}
    else openAction({action:'create',sleep_date:sleep.sleepDay,fields:{...payload,notes:[]}});
  };
  const handleQuickLog8h = () => { setSheetTimes(undefined); setShowSheet(true); };
  const handleSettingsSave = async () => {
    if (isSaving) return;
    const shown=settingsOriginal.current?.preferences??null;
    const displayed={sleep_target_hours:shown?.sleep_target_hours||'8',sleep_bedtime_reminder:shown?.sleep_bedtime_reminder??false,sleep_bedtime_reminder_time:shown?.sleep_bedtime_reminder_time||'22:30',sleep_wakeup_reminder:shown?.sleep_wakeup_reminder??false,sleep_wakeup_reminder_time:shown?.sleep_wakeup_reminder_time||'06:30'};
    const values={sleep_target_hours:settingsTarget,sleep_bedtime_reminder:smartAlarm,sleep_bedtime_reminder_time:reminderTime,sleep_wakeup_reminder:wakeupReminder,sleep_wakeup_reminder_time:wakeupReminderTime};
    const patch=Object.fromEntries(Object.entries(values).filter(([key,value])=>value!==(displayed as Record<string,unknown>)[key]));
    if(!Object.keys(patch).length){setShowSettings(false);return;}
    setShowSettings(false);openAction({action:'save_preferences',preferences_patch:patch,expected_preferences_snapshot:settingsOriginal.current??null});
  };

  const toggleNote = (n: SleepNote) => setNotes(prev => prev.includes(n) ? prev.filter(x => x !== n) : [...prev, n]);

  // ── Sleep history (chronological, per-night edit + gated delete) ──────────
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLogs, setHistoryLogs] = useState<SleepLog[] | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [editRecord, setEditRecord] = useState<SleepLog | null>(null);
  const [editRecBed, setEditRecBed] = useState("");
  const [editRecWake, setEditRecWake] = useState("");
  const [editRecQuality, setEditRecQuality] = useState<SleepQuality | null>(null);
  const [savingRecord, setSavingRecord] = useState(false);

  const [historyError, setHistoryError] = useState(false);
  const historyRequest = useRef(0);
  const reloadSleepHistory = useCallback(async () => {
    const ticket = ++historyRequest.current;
    const current = capture(); setHistoryLoading(true);
    try {
      const rows = await getSleepHistory(365);
      if (current() && ticket === historyRequest.current) { setHistoryLogs(rows); setHistoryError(false); }
    } catch (error) {
      console.error("[Sleep] history read failed", error);
      if (current() && ticket === historyRequest.current) setHistoryError(true);
    } finally { if (current() && ticket === historyRequest.current) setHistoryLoading(false); }
  }, [capture, getSleepHistory]);
  const toggleSleepHistory = () => setHistoryOpen(open => !open);
  useEffect(() => { void reloadSleepHistory(); return () => { historyRequest.current++; }; }, [reloadSleepHistory, checkedAt]);

  // Shared analytics engine — avg duration/score + debt trend by day/week/month
  // + projection. Streak stays in stats above (no streak metric in the config).
  const trendNow = useMemo(() => Date.parse(sleep.sleepDay + "T12:00:00"), [sleep.sleepDay]);
  const analyticsNow = useMemo(() => Date.parse(sleep.sleepDay + "T23:59:59.999"), [sleep.sleepDay]);
  const [sleepGran, setSleepGran] = useState<Granularity>(SLEEP_DASHBOARD.defaultGranularity);
  const sleepAnalytics = useMemo(
    () => buildDashboard(extractSleepSamples(historyLogs ?? []), SLEEP_DASHBOARD, sleepGran, analyticsNow),
    [historyLogs, sleepGran, analyticsNow],
  );

  // ── Recuperação e Prontidão: CÁLCULO, não sensor ────────────────────────
  // As duas entradas já estão no banco e já são lidas por esta tela e pelo
  // hook de métricas — `historyLogs` (365 dias, pré-carregado acima, UMA
  // busca) e `health_metrics` (90 dias). Nenhuma permissão nova, nenhuma
  // requisição nova: o que faltava era FUNÇÃO.
  //
  // A conta inteira mora em lib/health/recovery.ts, pura e testada em
  // vitest sem browser. Aqui só entra o encanamento.
  const { metrics: healthRows } = useHealthMetrics();
  const scoreInput = useMemo(() => ({
    nights: (historyLogs ?? []).map((l) => ({
      date: l.sleep_date,
      bedtime: l.bedtime,
      wakeup: l.wakeup,
      durationHours: sleepDurationValue(l.duration_hours),
      score: sleepScoreValue(l.score),
    })),
    hrv: toMetricPoints(healthRows, "hrv"),
    heartRate: toMetricPoints(healthRows, "heart_rate"),
    targetHours: parseFloat(String(targetHours)) || 8,
    now: Date.now(),
  }), [historyLogs, healthRows, targetHours]);

  // As noites no formato do módulo de tendência. Mesma fonte de scoreInput
  // (historyLogs, já pré-carregado) — nenhuma busca nova.
  const trendNights: TrendNight[] = useMemo(
    () => (historyLogs ?? []).map((l) => ({
      date: l.sleep_date,
      bedtime: l.bedtime,
      wakeup: l.wakeup,
      durationHours: sleepDurationValue(l.duration_hours),
      score: sleepScoreValue(l.score),
    })),
    [historyLogs],
  );
  // Numerator and denominator come from the same confirmed civil-day window.
  const debtWindow = { minutes: stats.sleepDebt, nights: stats.durationNights, days: 7 };
  const lastDuration = sleepDurationValue(lastNightSleep?.duration_hours);
  const lastScore = sleepScoreValue(lastNightSleep?.score);
  const weekAverageDuration = sleepDurationValue(stats.avgDuration);
  const durationLabel = (value: unknown) => {
    const duration = sleepDurationValue(value);
    return duration === null ? t("sleep.durationMissing") : formatDuration(duration);
  };

  const recovery = useMemo(() => recoveryScore(scoreInput), [scoreInput]);
  const readiness = useMemo(() => readinessScore(scoreInput), [scoreInput]);

  const openHistoryAction = (log:SleepLog,action:'update'|'delete') => { const snapshot=snapshotForSleep(log,userId??undefined);if(!snapshot){toast.error(t('sleepAction.readOnly'));return;}openAction({action,sleep_id:log.id,expected_snapshot:snapshot}); };
  const openRecordEdit = (log:SleepLog) => openHistoryAction(log,'update');
  const saveRecordEdit = async () => { if(!editRecord||!editRecQuality)return;const snapshot=snapshotForSleep(editRecord,userId??undefined);if(!snapshot)return;openAction({action:'update',sleep_id:editRecord.id,expected_snapshot:snapshot,fields:editPatch(editRecord,{bedtime:editRecBed,wakeup:editRecWake,quality:editRecQuality})});setEditRecord(null); };
  const handleDelete = async () => { if(!lastNightSleep||isSaving)return;setDeleteOpen(false);openHistoryAction(lastNightSleep,'delete'); };
  const onActionConfirmed = async (response:SleepResponse,request:SleepRequest) => {
    const current=confirmedActor;if(!current())return;setEditing(false);setEditRecord(null);toast.success(t(request.action==='save_preferences'?'sleep.settingsSaved':request.action==='delete'?'sleep.edit.deleteSuccess':'sleep.saved'));
    await showReceiptRewards(response,current);await refresh();if(current())await reloadSleepHistory();
    if(current()&&userId&&request.action==='save_preferences'&&(response.preferences?.sleep_bedtime_reminder||response.preferences?.sleep_wakeup_reminder)){try{const outcome=await requestPushPermission({ownerId:userId,isCurrent:current});if(current()&&outcome!=='granted')toast.warning(t('sleep.notificationPermissionDenied'));}catch{if(current())toast.warning(t('sleep.notificationPermissionDenied'));}}
  };

  const stagger = (i: number) => ({ delay: 0.06 + i * 0.08, duration: 0.5 });

  // Inline form is for EDITING an existing log only. The empty-state cyan
  // CTA opens SleepLogSheet (the single capture flow). Previous behavior
  // (`!lastNightSleep || editing`) caused a duplicate inline form to render
  // below the empty-state card — Rafa flagged 25/04 18:11 on iPhone Safari.
  const showForm = editing;

  return (
    <MobileLayout className="pb-28 pt-2 relative overflow-hidden">
      {/* Deep space ambient + particles */}
      <div className="absolute inset-0 pointer-events-none z-0" style={{ background: "linear-gradient(180deg, #0a0a0f 0%, #0d1117 50%, #0a0a0f 100%)" }}>
        <div style={{ position: "absolute", top: "-10%", left: "50%", transform: "translateX(-50%)", width: 400, height: 400, borderRadius: "50%", background: "radial-gradient(circle, hsl(220 70% 20% / 0.30) 0%, hsl(265 50% 15% / 0.10) 50%, transparent 70%)" }} />
        <div style={{ position: "absolute", bottom: "20%", right: "-10%", width: 250, height: 250, borderRadius: "50%", background: "radial-gradient(circle, hsl(265 60% 30% / 0.18) 0%, transparent 70%)" }} />
        <div style={{ position: "absolute", top: "40%", left: "-5%", width: 180, height: 180, borderRadius: "50%", background: "radial-gradient(circle, hsl(187 72% 50% / 0.06) 0%, transparent 70%)" }} />
      </div>


      {/* BACK */}
      <MirrorBackButton className="mb-2" />

      {/* HEADER */}
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="flex items-center justify-between mb-3 relative z-10">
        <div>
          <p className="text-[9px] font-display tracking-[0.22em] uppercase mb-0.5" style={{ color: "hsl(220 70% 65% / 0.65)" }}>{t("sleep.subtitle")}</p>
          <div className="flex items-center gap-2">
            <h1 className="font-display text-[28px] font-semibold text-foreground leading-none">{t("sleep.title")}</h1>
            <InfoTooltip title={t("tooltips.sleep.title")} description={t("tooltips.sleep.description")} />
          </div>
        </div>
        <button aria-label={t("sleep.settings")} onClick={() => setShowSettings(true)} className="w-9 h-9 rounded-xl flex items-center justify-center active:scale-95" style={{ background: "hsl(220 50% 50% / 0.12)", border: "1px solid hsl(220 50% 50% / 0.25)" }}>
          <Settings size={15} style={{ color: "hsl(220 70% 65%)" }} />
        </button>
      </motion.div>

      {status === "error" && <div role="alert" className="relative z-10 mb-4 rounded-xl border border-amber-500/40 p-3 text-sm">
        <p>{t("sleep.loadError")}</p><button disabled={sleep.isRefreshing} onClick={() => void refresh()} className="min-h-11 underline">{t("common.retry")}</button>
      </div>}
      {(historyError || historyLoading && historyLogs === null) && <div role="status" className="relative z-10 mb-4 rounded-xl border border-border p-3 text-sm">
        <p>{t(historyError ? "sleep.historyError" : "common.loading")}</p>
        {historyError && <button disabled={historyLoading} onClick={() => void reloadSleepHistory()} className="min-h-11 underline">{t("common.retry")}</button>}
      </div>}
      {/* TAB BAR */}
      <div className="flex gap-0 mb-5 relative z-10 rounded-xl p-1" style={{ background: "hsl(220 40% 10% / 0.50)", backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)", border: "1px solid hsl(220 50% 30% / 0.15)" }}>
        {(["log", "insights"] as const).map(tab => (
          <button key={tab} aria-pressed={activeTab === tab} onClick={() => setActiveTab(tab)}
            className="flex-1 py-2.5 text-center font-display text-[13px] font-semibold tracking-wide transition-colors relative z-10"
            style={{ color: activeTab === tab ? ACCENT : "hsl(218 14% 45%)" }}>
            {t(`sleep.tab.${tab}`)}
            {activeTab === tab && (
              <motion.div layoutId="sleepTabUnderline" className="absolute bottom-0 left-[15%] right-[15%] h-[2px] rounded-full"
                style={{ background: ACCENT, boxShadow: `0 2px 16px ${ACCENT}70, 0 0 8px ${ACCENT}50` }} />
            )}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {/* ═══════════════ TAB: LOG ═══════════════ */}
        {activeTab === "log" && (
          <motion.div key="tab-log" className="relative z-10" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }}>

            {/* XP Bar */}
            <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.4 }}
              className="flex items-center gap-3 rounded-xl px-3 py-2.5 mb-4"
              style={{ background: "linear-gradient(135deg, hsl(220 50% 10%), hsl(220 35% 8%))", border: "1px solid hsl(220 70% 55% / 0.20)" }}>
              <span className="px-2.5 py-1 rounded-full font-display text-[10px] font-bold shrink-0"
                style={{ background: "hsl(220 70% 55% / 0.15)", border: "1px solid hsl(220 70% 55% / 0.35)", color: "hsl(220 70% 70%)", textShadow: "0 0 8px hsl(220 70% 50% / 0.40)" }}>
                {tf("habits.xp.level", { level: String(level) })}
              </span>
              <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: "hsl(218 14% 18%)" }}>
                <motion.div className="h-full rounded-full" initial={{ width: 0 }} animate={{ width: `${getLevelProgress()}%` }}
                  transition={{ delay: 0.4, duration: 0.9, ease: "easeOut" }}
                  style={{ background: "linear-gradient(90deg, hsl(220 70% 45%), hsl(220 70% 65%))", boxShadow: "0 0 8px hsl(220 70% 55% / 0.40)" }} />
              </div>
              <span className="text-[9px] font-display shrink-0" style={{ color: "hsl(218 14% 55%)" }}>{totalXP} XP</span>
              <motion.div animate={{ opacity: [0.4, 1, 0.4], scale: [1, 1.2, 1] }} transition={{ duration: 3, repeat: reducedMotion ? 0 : Infinity, ease: "easeInOut" }}>
                <Moon size={12} style={{ color: "hsl(220 70% 65%)" }} />
              </motion.div>
            </motion.div>

            {/* ── A LEITURA DO ESPELHO, NO TOPO ──────────────────────────
                Ela já existia e já rodava — só que ANINHADA dentro do card do
                score, abaixo do anel. A vantagem competitiva do produto
                renderizava como subordinada da nota do dia. */}
            <MirrorSleepCard
              reaction={mirrorReact.reaction}
              isLoading={mirrorReact.isLoading}
              error={mirrorReact.error}
              logId={lastNightSleep?.id ?? null}
            />

            {/* ── A TENDÊNCIA MANDA NA HIERARQUIA ────────────────────────
                O score do dia FICA (é a Section A logo abaixo), mas deixa de
                ser o primeiro número da dobra. Regra da casa: "um score
                diário não significa nada, uma tendência de 5 dias significa
                tudo" — e a pesquisa chama o contrário de ORTOSSONIA. */}
            <SleepTrendSummary nights={trendNights} now={trendNow} compact />

            {/* Section A — LAST NIGHT HERO */}
            {lastNightSleep && !editing ? (
              <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={stagger(0)}
                className="rounded-3xl p-6 mb-5 relative overflow-hidden text-center"
                style={{ background: "linear-gradient(145deg, hsl(225 50% 5%), hsl(230 40% 3%))", border: "1.5px solid hsl(220 50% 40% / 0.25)", boxShadow: `0 0 50px hsl(220 70% 30% / 0.20), inset 0 0 40px hsl(265 50% 15% / 0.10), 0 0 80px ${ACCENT}05` }}>
                {/* Star field */}
                <div className="absolute inset-0 pointer-events-none">
                  {Array.from({ length: 50 }, (_, i) => (
                    <div key={i} className="absolute rounded-full" style={{ width: Math.random() > 0.7 ? 1.5 : 0.8, height: Math.random() > 0.7 ? 1.5 : 0.8, background: i % 4 === 0 ? ACCENT : i % 4 === 1 ? "hsl(265 60% 80%)" : "hsl(220 80% 90%)", opacity: Math.random() * 0.45 + 0.1, left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%` }} />
                  ))}
                </div>
                {/* Nebula gradient overlay */}
                <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at 50% 40%, hsl(265 50% 20% / 0.25) 0%, hsl(220 60% 15% / 0.15) 40%, transparent 70%)" }} />
                {/* Radial glow behind score ring */}
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none" style={{ width: 200, height: 200, borderRadius: "50%", background: `radial-gradient(circle, hsl(220 70% 30% / 0.35) 0%, hsl(265 50% 20% / 0.12) 50%, transparent 70%)` }} />
                {/* Ring pulses */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div style={{ width: 180, height: 180, borderRadius: "50%", border: "1px solid hsl(220 70% 50% / 0.06)", position: "absolute", animation: "ringPulse 4s ease-in-out infinite" }} />
                  <div style={{ width: 140, height: 140, borderRadius: "50%", border: "1px solid hsl(265 60% 50% / 0.08)", position: "absolute", animation: "ringPulse 4s ease-in-out infinite 0.5s" }} />
                </div>
                <p className="text-[9px] font-display tracking-[0.22em] uppercase mb-4" style={{ color: "hsl(220 70% 70% / 0.70)" }}>{t("sleep.sleepScore")}</p>
                <div className="relative mx-auto mb-4" style={{ width: 120, height: 120 }}>
                  <svg width="120" height="120" viewBox="0 0 120 120" className="absolute inset-0">
                    <circle cx="60" cy="60" r="52" fill="none" stroke="hsl(220 50% 30% / 0.25)" strokeWidth="6" />
                    {lastScore !== null && <motion.circle cx="60" cy="60" r="52" fill="none" stroke="url(#scoreGrad)" strokeWidth="6" strokeLinecap="round"
                      filter="url(#scoreGlow)"
                      strokeDasharray={`${2 * Math.PI * 52}`} initial={{ strokeDashoffset: 2 * Math.PI * 52 }}
                      animate={{ strokeDashoffset: 2 * Math.PI * 52 * (1 - lastScore / 100) }}
                      transition={{ delay: 0.4, duration: 1.4, ease: [0.25, 0.46, 0.45, 0.94] }} transform="rotate(-90 60 60)" />}
                    <defs>
                      <linearGradient id="scoreGrad" x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" stopColor="hsl(220 70% 55%)" /><stop offset="100%" stopColor={ACCENT} /></linearGradient>
                      <filter id="scoreGlow"><feGaussianBlur stdDeviation="5" result="blur" /><feMerge><feMergeNode in="blur" /><feMergeNode in="blur" /><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
                    </defs>
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="font-display text-[36px] font-bold text-white leading-none" style={{ textShadow: `0 0 20px ${ACCENT}50` }}>{lastScore ?? "—"}</span>
                    <span className="text-[9px] font-display tracking-wide" style={{ color: "hsl(220 70% 70% / 0.70)" }}>{lastScore === null ? t("sleep.scoreMissing") : "/ 100"}</span>
                  </div>
                </div>
                <p className="font-display text-[18px] font-bold text-white mb-1" style={{ textShadow: `0 0 24px ${ACCENT}40` }}>{lastDuration === null ? t("sleep.durationMissing") : formatDuration(lastDuration)}</p>
                <div className="flex items-center gap-1.5 flex-wrap justify-center mb-4">
                  {lastNightSleep.quality && (
                    <span className="inline-block px-3 py-1 rounded-full font-display text-[10px] font-semibold"
                      style={{ background: `${QUALITY_COLORS[lastNightSleep.quality]}18`, color: QUALITY_COLORS[lastNightSleep.quality], border: `1px solid ${QUALITY_COLORS[lastNightSleep.quality]}35`, boxShadow: `inset 0 0 12px ${QUALITY_COLORS[lastNightSleep.quality]}15, 0 0 8px ${QUALITY_COLORS[lastNightSleep.quality]}10` }}>
                      {t(`sleep.quality.${lastNightSleep.quality}`)}
                    </span>
                  )}
                  {/* Source badge — surfaces non-manual logs so the user
                      knows where the night came from. Manual logs stay
                      unbadged to keep the card uncluttered for the
                      common case. */}
                  {lastNightSleep.source && lastNightSleep.source !== "manual" && (
                    <span
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-full font-display text-[9px] font-bold tracking-wider uppercase"
                      style={{
                        background: "hsl(180 65% 50% / 0.15)",
                        color: "hsl(180 65% 65%)",
                        border: "1px solid hsl(180 65% 50% / 0.35)",
                      }}
                    >
                      {t("sleep.source." + lastNightSleep.source)}
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2 mb-3">
                  {[
                    { icon: Moon, label: t("sleep.bedtime"), value: lastNightSleep.bedtime.slice(0, 5), color: "hsl(220 70% 65%)" },
                    { icon: Zap, label: t("sleep.wakeUp"), value: lastNightSleep.wakeup.slice(0, 5), color: ACCENT },
                  ].map((m, i) => (
                    <div key={i} className="rounded-2xl p-2.5 flex flex-col items-center gap-1" style={{ background: "hsl(220 50% 12% / 0.40)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)", border: `1px solid ${m.color}25`, boxShadow: `0 0 12px ${m.color}08` }}>
                      <m.icon size={12} style={{ color: m.color }} />
                      <p className="font-display text-[13px] font-bold text-white">{m.value}</p>
                      <p className="text-[8px] font-body text-center" style={{ color: "hsl(218 14% 60%)" }}>{m.label}</p>
                    </div>
                  ))}
                </div>

                <button onClick={() => setEditing(true)} className="font-display text-[11px] font-medium" style={{ color: ACCENT }}>
                  {t("sleep.edit")}
                </button>
              </motion.div>
            ) : !lastNightSleep && !editing ? (
              <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={stagger(0)}
                className="rounded-3xl p-6 pb-6 mb-5 text-center relative overflow-hidden flex flex-col items-center"
                style={{ background: "linear-gradient(145deg, hsl(220 35% 12%), hsl(225 35% 6%))", border: "1.5px solid hsl(220 50% 50% / 0.25)", boxShadow: `0 0 30px hsl(220 70% 40% / 0.10), 0 0 60px ${ACCENT}06` }}>
                {/* Decorative moon — reclaims presence without becoming protagonist. */}
                <div className="relative mb-8" style={{ width: 72, height: 72, opacity: 0.6 }}>
                  <motion.div
                    animate={{ scale: [0.95, 1.05, 0.95], opacity: [0.6, 1, 0.6] }}
                    transition={{ duration: 4, repeat: reducedMotion ? 0 : Infinity, ease: "easeInOut" }}
                    className="absolute inset-0 flex items-center justify-center">
                    <svg width="46" height="46" viewBox="0 0 48 48" fill="none">
                      <defs>
                        <radialGradient id="moonGlow" cx="50%" cy="50%" r="50%">
                          <stop offset="0%" stopColor={ACCENT} stopOpacity="0.3" />
                          <stop offset="100%" stopColor={ACCENT} stopOpacity="0" />
                        </radialGradient>
                      </defs>
                      <circle cx="24" cy="24" r="24" fill="url(#moonGlow)" />
                      <path d="M24 4C14 4 6 12 6 22s8 18 18 18c4 0 7.5-1.3 10.5-3.5C31 39 26 36 22 30s-4-14 0-20C23 8 23.5 6 24 4z" fill="hsl(220 70% 65%)" fillOpacity="0.8" />
                    </svg>
                  </motion.div>
                  {/* Orbiting stars — subtle cinematic layer. */}
                  {[0, 1, 2, 3].map(s => (
                    <motion.div
                      key={s}
                      className="absolute rounded-full"
                      style={{
                        width: 2,
                        height: 2,
                        background: s % 2 === 0 ? ACCENT : "hsl(265 60% 75%)",
                        left: "50%",
                        top: "50%",
                      }}
                      animate={{
                        x: [
                          Math.cos((s * Math.PI) / 2) * 40,
                          Math.cos((s * Math.PI) / 2 + Math.PI) * 40,
                          Math.cos((s * Math.PI) / 2) * 40,
                        ],
                        y: [
                          Math.sin((s * Math.PI) / 2) * 40,
                          Math.sin((s * Math.PI) / 2 + Math.PI) * 40,
                          Math.sin((s * Math.PI) / 2) * 40,
                        ],
                        opacity: [0.3, 0.8, 0.3],
                      }}
                      transition={{ duration: 6 + s, repeat: reducedMotion ? 0 : Infinity, ease: "easeInOut", delay: s * 0.5 }}
                    />
                  ))}
                </div>
                <p className="font-display text-[13px] font-semibold text-white mb-7" style={{ textShadow: `0 0 20px ${ACCENT}30` }}>{t("sleep.notLogged")}</p>

                {/* Review times before recording this night. */}
                <motion.button
                  onClick={handleQuickLog8h} disabled={isSaving}
                  whileTap={{ scale: 0.97 }}
                  className="rounded-2xl font-display text-[16px] font-bold text-white relative overflow-hidden mx-auto flex flex-col items-center justify-center"
                  style={{
                    width: "72%",
                    paddingTop: 16,
                    paddingBottom: 16,
                    background: `linear-gradient(135deg, ${ACCENT}, hsl(200 80% 35%))`,
                    boxShadow: `0 0 20px ${ACCENT}30, inset 0 0 12px ${ACCENT}08`,
                  }}
                >
                  <motion.div
                    className="absolute inset-0 rounded-2xl pointer-events-none"
                    animate={{ backgroundPosition: ["0% 50%", "200% 50%"] }}
                    transition={{ duration: 3, repeat: reducedMotion ? 0 : Infinity, ease: "linear" }}
                    style={{
                      background: `linear-gradient(90deg, transparent, rgba(255,255,255,0.12), transparent)`,
                      backgroundSize: "200% 100%",
                    }}
                  />
                  <span className="relative z-10">{t("sleep.reviewNight")}</span>
                  <span className="relative z-10 font-body text-[10px] font-medium opacity-80 mt-0.5">
                    {t("sleep.reviewNightHint")}
                  </span>
                </motion.button>

                {/* Secondary row. */}
                <div className="flex gap-2 mt-4" style={{ width: "85%" }}>
                  {hasHistory && (
                    <button
                      onClick={handleQuickLog} disabled={isSaving}
                      className="flex-1 py-2.5 rounded-xl font-display text-[12px] font-medium text-white/80 active:scale-[0.97]"
                      style={{
                        background: "hsl(220 50% 12% / 0.50)",
                        border: "1px solid hsl(220 50% 35% / 0.30)",
                      }}
                    >
                      {t("sleep.reviewPrevious")}
                    </button>
                  )}
                  <button
                    onClick={() => navigate("/settings/sleep")}
                    className="flex-1 py-2.5 rounded-xl font-display text-[12px] font-medium text-white/80 active:scale-[0.97]"
                    style={{
                      background: "hsl(220 50% 12% / 0.50)",
                      border: "1px solid hsl(220 50% 35% / 0.30)",
                    }}
                  >
                    {t("sleep.connectWearable")}
                  </button>
                </div>
              </motion.div>
            ) : null}

            {/* Section B — LOG FORM */}
            <AnimatePresence>
              {showForm && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.3 }}
                  className="rounded-2xl p-4 mb-5 relative overflow-hidden overflow-x-hidden"
                  style={{ background: "linear-gradient(145deg, hsl(220 35% 12%), hsl(225 35% 6%))", border: "1px solid hsl(220 50% 45% / 0.30)", maxWidth: "100%" }}>
                  <p className="text-[9px] font-display tracking-[0.18em] uppercase mb-4" style={{ color: "hsl(220 70% 70% / 0.70)" }}>{editing ? t("sleep.edit") : t("sleep.logSleep")}</p>

                  {/* Time pickers */}
                  <div className="grid grid-cols-2 gap-2 mb-4">
                    {[{ label: t("sleep.bedtime"), value: bedtime, onChange: setBedtime }, { label: t("sleep.wakeUp"), value: wakeup, onChange: setWakeup }].map(f => (
                      <div key={f.label} className="min-w-0">
                        <p className="text-[9px] font-display tracking-[0.14em] uppercase mb-1.5" style={{ color: "hsl(218 14% 60%)" }}>{f.label}</p>
                        <input type="time" value={f.value} onChange={e => f.onChange(e.target.value)}
                          className="w-full min-w-0 rounded-xl px-2.5 py-2.5 font-display text-[13px] font-semibold text-white focus:outline-none"
                          style={{ background: "hsl(220 50% 12% / 0.60)", border: "1px solid hsl(220 50% 35% / 0.30)", colorScheme: "dark", maxWidth: "100%", boxSizing: "border-box" }} />
                      </div>
                    ))}
                  </div>

                  {/* Quality selector */}
                  <p className="text-[9px] font-display tracking-[0.14em] uppercase mb-2" style={{ color: "hsl(218 14% 60%)" }}>{t("sleep.quality.question")}</p>
                  <div className="flex gap-2 mb-4 justify-center">
                    {QUALITY_OPTIONS.map(opt => {
                      const Icon = opt.icon;
                      const selected = quality === opt.value;
                      return (
                        <motion.button key={opt.value} onClick={() => setQuality(opt.value)}
                          whileTap={{ scale: 0.92 }}
                          animate={selected ? { scale: [1, 1.12, 1.05] } : { scale: 1 }}
                          transition={selected ? { duration: 0.25 } : { duration: 0.15 }}
                          className="flex flex-col items-center gap-1.5"
                          style={{ width: 56 }}>
                          <div className="w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300"
                            style={{
                              background: selected ? `linear-gradient(135deg, ${opt.color}25, ${opt.color}10)` : `${opt.color}08`,
                              border: `2px solid ${selected ? opt.color : `${opt.color}20`}`,
                              boxShadow: selected ? `0 0 18px ${opt.color}35, inset 0 0 10px ${opt.color}12` : "none",
                            }}>
                            <Icon size={18} style={{ color: selected ? opt.color : opt.color, opacity: selected ? 1 : 0.3, transition: "all 0.2s" }} />
                          </div>
                          <span className="text-[8px] font-display font-semibold transition-colors" style={{ color: selected ? opt.color : "hsl(218 14% 40%)" }}>
                            {t(opt.key)}
                          </span>
                        </motion.button>
                      );
                    })}
                  </div>

                  {/* Sleep notes */}
                  <p className="text-[9px] font-display tracking-[0.14em] uppercase mb-2" style={{ color: "hsl(218 14% 60%)" }}>{t("sleep.notes.title")}</p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {NOTE_OPTIONS.map(opt => {
                      const selected = notes.includes(opt.value);
                      const emoji = NOTE_ICONS[opt.value] || "";
                      return (
                        <motion.button key={opt.value} onClick={() => toggleNote(opt.value)}
                          whileTap={{ scale: 0.93 }}
                          animate={selected ? { scale: [1, 1.08, 1.02] } : {}}
                          transition={{ duration: 0.2 }}
                          className="px-3.5 py-2 rounded-full font-display text-[10px] font-medium transition-all"
                          style={{
                            background: selected ? `${ACCENT}18` : "hsl(220 50% 12% / 0.50)",
                            border: `1px solid ${selected ? ACCENT : "hsl(220 50% 35% / 0.30)"}`,
                            color: selected ? "white" : "hsl(218 14% 50%)",
                            boxShadow: selected ? `0 0 12px ${ACCENT}25` : "none",
                          }}>
                          <span className="mr-1">{emoji}</span>{t(opt.key)}
                        </motion.button>
                      );
                    })}
                  </div>

                  {/* Free text note with voice-to-text */}
                  <div className="flex items-center gap-2 mb-4">
                    <input type="text" value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="..."
                      className="flex-1 min-w-0 rounded-xl px-3 py-2 font-body text-[12px] text-white focus:outline-none"
                      style={{ background: "hsl(220 50% 12% / 0.60)", border: "1px solid hsl(220 50% 35% / 0.20)", colorScheme: "dark" }} />
                    {voiceInput.supported && (
                      <motion.button
                        type="button"
                        onClick={async () => {
                          if (voiceInput.isListening) {
                            await voiceInput.stopListening();
                          } else {
                            await voiceInput.startListening();
                          }
                        }}
                        whileTap={{ scale: 0.92 }}
                        animate={voiceInput.isListening
                          ? { boxShadow: [
                              `0 0 0 0 ${ACCENT}00`,
                              `0 0 0 ${Math.max(4, voiceInput.amplitude * 14)}px ${ACCENT}30`,
                              `0 0 0 0 ${ACCENT}00`,
                            ] }
                          : {}}
                        transition={{ duration: 0.9, repeat: reducedMotion ? 0 : Infinity, ease: "easeInOut" }}
                        className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                        style={{
                          background: voiceInput.isListening ? `${ACCENT}26` : "hsl(220 50% 12% / 0.60)",
                          border: `1px solid ${voiceInput.isListening ? `${ACCENT}80` : "hsl(220 50% 35% / 0.30)"}`,
                          color: voiceInput.isListening ? ACCENT : "hsl(218 14% 65%)",
                        }}
                        aria-label="Voice note"
                      >
                        <Mic size={14} />
                      </motion.button>
                    )}
                  </div>

                  {/* Save */}
                  <motion.button onClick={handleSave} disabled={saving || isSaving || !bedtime || !wakeup || !quality}
                    whileTap={{ scale: 0.97 }}
                    className="w-full py-3 rounded-xl font-display text-[13px] font-semibold tracking-wide transition-all disabled:opacity-40 relative overflow-hidden"
                    style={{ background: "linear-gradient(135deg, hsl(220 70% 52%), hsl(220 70% 42%))", color: "#fff", boxShadow: `0 4px 24px hsl(220 70% 50% / 0.40), 0 0 20px ${ACCENT}18` }}>
                    <motion.div className="absolute inset-0" animate={{ backgroundPosition: ["0% 50%", "200% 50%"] }}
                      transition={{ duration: 2.5, repeat: reducedMotion ? 0 : Infinity, ease: "linear" }}
                      style={{ background: "linear-gradient(90deg, transparent 0%, hsla(220, 100%, 85%, 0.18) 50%, transparent 100%)", backgroundSize: "200% 100%" }} />
                    <span className="relative z-10">{saving ? t("sleep.saving") : t("sleep.save")}</span>
                  </motion.button>
                  {editing && (
                    <>
                      {/* O botão de apagar agora aparece SEMPRE, inclusive
                          para noite vinda do relógio: a exclusão virou durável
                          (lápide em integration_deletions). Antes havia aqui um
                          texto explicando por que não dava para apagar. */}
                      <button
                        type="button"
                        onClick={() => setDeleteOpen(true)}
                        disabled={deleting}
                        className="w-full mt-2 py-2.5 rounded-xl font-display text-[12px] font-semibold flex items-center justify-center gap-1.5 disabled:opacity-50 active:scale-[0.97]"
                        style={{
                          background: "hsl(0 70% 50% / 0.10)",
                          border: "1px solid hsl(0 70% 50% / 0.35)",
                          color: "hsl(0 70% 65%)",
                        }}
                      >
                        <Trash2 size={13} />
                        <span>{t("sleep.edit.delete")}</span>
                      </button>
                      <button onClick={() => setEditing(false)} className="w-full mt-2 py-2 font-display text-[11px] font-medium" style={{ color: "hsl(218 14% 62%)" }}>
                        {t("common.cancel")}
                      </button>
                    </>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Section C — THIS WEEK */}
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={stagger(2)}
              className="rounded-2xl p-4 mb-5 relative overflow-hidden" style={{ background: "linear-gradient(145deg, hsl(220 35% 12%), hsl(225 35% 6%))", border: "1px solid hsl(220 50% 45% / 0.30)" }}>
              <div className="flex items-center justify-between mb-4">
                <p className="text-[9px] font-display tracking-[0.18em] uppercase" style={{ color: "hsl(220 70% 70% / 0.70)" }}>{t("sleep.thisWeek")}</p>
                {weekAverageDuration !== null && (
                  <div className="flex items-center gap-1.5">
                    <TrendingUp size={11} style={{ color: ACCENT }} />
                    <p className="text-[10px] font-display font-semibold" style={{ color: ACCENT }}>{t("sleep.avg")} {weekAverageDuration}h</p>
                  </div>
                )}
              </div>
              <div className="flex gap-2 items-end relative" style={{ height: 70 }}>
                {/* Target line with pulse */}
                <motion.div className="absolute left-0 right-0 border-t border-dashed"
                  animate={{ opacity: [0.4, 0.7, 0.4] }}
                  transition={{ duration: 3, repeat: reducedMotion ? 0 : Infinity, ease: "easeInOut" }}
                  style={{ bottom: `${(targetHours / 10) * 100}%`, borderColor: `${ACCENT}50` }}>
                  <span className="absolute -top-3 right-0 text-[7px] font-display" style={{ color: `${ACCENT}60` }}>{targetHours}h</span>
                </motion.div>
                {stats.weekLogs.map((log, i) => {
                  const hours = sleepDurationValue(log?.duration_hours);
                  const pct = hours === null ? 0 : Math.min(100, (hours / 10) * 100);
                  const isToday = i === 6;
                  const qualityColor = log?.quality ? QUALITY_COLORS[log.quality] : undefined;
                  return (
                    <button key={i} onClick={() => setActiveDay(i)} aria-label={`${stats.weekDayLabels[i] || "—"} — ${log ? durationLabel(log.duration_hours) : t("sleep.notLogged")}`} className="flex-1 flex flex-col items-center gap-1 active:opacity-80 touch-manipulation">
                      <div className="w-full relative rounded-t-lg overflow-hidden transition-all duration-300" style={{ height: `${pct}%`, minHeight: hours === null ? 0 : hours > 0 ? 8 : 2 }}>
                        <motion.div className="absolute inset-0 rounded-t-lg"
                          initial={{ scaleY: 0 }} animate={{ scaleY: 1 }}
                          transition={{ delay: 0.3 + i * 0.05, duration: 0.6, ease: "easeOut" }}
                          style={{
                            transformOrigin: "bottom",
                            background: isToday
                              ? `linear-gradient(180deg, ${ACCENT}, hsl(220 70% 35%))`
                              : qualityColor
                                ? `linear-gradient(180deg, ${qualityColor}cc, ${qualityColor}30)`
                                : "linear-gradient(180deg, hsl(220 50% 40% / 0.5), hsl(220 50% 18% / 0.3))",
                            boxShadow: isToday ? `0 0 16px ${ACCENT}50` : i === activeDay ? `0 0 10px ${(qualityColor || "hsl(220 50% 40%)")}30` : "none",
                          }} />
                        {/* Score dot on top */}
                        {hours !== null && (
                          <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full" style={{ background: qualityColor || ACCENT, boxShadow: `0 0 6px ${qualityColor || ACCENT}60` }} />
                        )}
                      </div>
                      <p className="text-[8px] font-display font-medium" style={{ color: isToday ? ACCENT : i === activeDay ? "hsl(218 14% 60%)" : "hsl(218 14% 40%)" }}>
                        {stats.weekDayLabels[i] || "-"}
                      </p>
                    </button>
                  );
                })}
              </div>
              <AnimatePresence mode="wait">
                <motion.div key={activeDay} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}
                  className="mt-3 flex items-center justify-between px-3 py-2 rounded-xl" style={{ background: "hsl(220 50% 12% / 0.50)", border: "1px solid hsl(220 50% 35% / 0.30)" }}>
                  {stats.weekLogs[activeDay] ? (
                    <>
                      <p className="font-display text-[12px] font-bold text-white">
                        {stats.weekDayLabels[activeDay]} — {durationLabel(stats.weekLogs[activeDay]!.duration_hours)}
                      </p>
                      <span className="font-display text-[11px] font-bold" style={{ color: ACCENT }}>
                        {sleepScoreValue(stats.weekLogs[activeDay]!.score) === null ? t("sleep.scoreMissing") : t("sleep.score") + " " + sleepScoreValue(stats.weekLogs[activeDay]!.score)}
                      </span>
                    </>
                  ) : (
                    <p className="font-display text-[12px] text-center w-full" style={{ color: "hsl(218 14% 55%)" }}>
                      {stats.weekDayLabels[activeDay]} — {t("sleep.notLogged")}
                    </p>
                  )}
                </motion.div>
              </AnimatePresence>
            </motion.div>

            {/* ⬆ EVOLUÇÃO MUDOU-SE PARA A ABA INSIGHTS.
                Os gráficos de 365 dias moravam aqui, no meio do "como foi
                ontem", empurrando o registro e a dívida para fora da tela.
                Log = a noite; Insights = a tendência. */}
            {/* Section C2 — SLEEP HISTORY (chronological; wearable rows keep
                edit but not delete — no tombstone, the sync would resurrect
                the day; manual rows delete with undo) */}
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={stagger(3)}
              className="rounded-2xl p-4 mb-5" style={{ background: "linear-gradient(145deg, hsl(220 35% 12%), hsl(225 35% 6%))", border: "1px solid hsl(220 50% 45% / 0.30)" }}>
              <button aria-expanded={historyOpen} onClick={toggleSleepHistory} className="w-full flex items-center justify-between">
                <span className="flex items-center gap-2 text-[9px] font-display tracking-[0.18em] uppercase" style={{ color: "hsl(220 70% 70% / 0.70)" }}>
                  <History size={11} />
                  {t("sleep.history.title")}
                </span>
                <ChevronLeft size={13} style={{ color: "hsl(220 70% 70% / 0.70)", transform: historyOpen ? "rotate(90deg)" : "rotate(-90deg)", transition: "transform 0.2s" }} />
              </button>
              {historyOpen && (
                <div className="mt-3">
                  {historyLoading && historyLogs === null ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 size={18} className="animate-spin" style={{ color: ACCENT }} />
                    </div>
                  ) : historyLogs !== null ? (
                    <ModuleHistoryList<SleepLog>
                      items={historyLogs ?? []}
                      getId={(l) => l.id}
                      getDate={(l) => l.sleep_date + "T12:00:00"}
                      renderItem={(l) => (
                        <div className="min-w-0">
                          <p className="text-sm text-white truncate">
                            {durationLabel(l.duration_hours) +
                              " · " + l.bedtime.slice(0, 5) + "–" + l.wakeup.slice(0, 5) +
                              (l.quality ? " · " + t("sleep.quality." + l.quality) : "")}
                          </p>
                          {snapshotForSleep(l,userId??undefined) && <button type="button" className="min-h-11 rounded-xl border border-border px-3 text-destructive" aria-label={t('common.delete')} onClick={()=>openHistoryAction(l,'delete')}>{t('common.delete')}</button>}
                          {l.source && l.source !== "manual" && (
                            <p className="text-[10px] mt-0.5" style={{ color: "hsl(218 14% 55%)" }}>
                              {t("sleep.source." + l.source)}
                            </p>
                          )}
                        </div>
                      )}
                      onEdit={openRecordEdit}
                      canEdit={log=>!!snapshotForSleep(log,userId??undefined)}
                      labels={{
                        empty: t("sleep.history.empty"),
                        deletedToast: t("moduleHistory.deletedToast"),
                        undo: t("moduleHistory.undo"),
                        period30: t("moduleHistory.period30"),
                        period90: t("moduleHistory.period90"),
                        periodAll: t("moduleHistory.periodAll"),
                      }}
                    />
                  ) : null}
                </div>
              )}
            </motion.div>

            {/* Section D — SLEEP DEBT.
                🔴 O NÚMERO GANHOU DENOMINADOR. Era "-15h28min deficit vs 8h
                target" em vermelho gigante sem dizer de QUE período — número
                sem janela é susto, não informação. A janela sempre foi 7 dias
                somando SÓ as noites registradas (useSleep:214); agora ela
                está escrita embaixo do número, com quantas noites entraram. */}
            {(() => {
              const debt = debtWindow.minutes;
              const debtColor = debt === null ? "hsl(218 14% 65%)" : debt <= 0 ? "hsl(142 60% 50%)" : debt > 300 ? "#ef4444" : "#f59e0b";
              const debtPct = debt === null ? 0 : Math.max(0, Math.min(100, (debt / 420) * 100));
              return (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={stagger(3)}
                  className="rounded-2xl p-4 mb-5 relative overflow-hidden" style={{ background: "linear-gradient(145deg, hsl(220 35% 12%), hsl(225 35% 6%))", border: `1px solid ${debtColor}20`, boxShadow: debt !== null && debt > 300 ? `0 0 20px ${debtColor}10` : "none" }}>
                  <div className="flex items-center gap-2 mb-2">
                    <Moon size={13} style={{ color: debtColor, opacity: 0.7 }} />
                    <p className="text-[9px] font-display tracking-[0.18em] uppercase" style={{ color: "hsl(220 70% 70% / 0.70)" }}>{t("sleep.debt.title")}</p>
                  </div>
                  {debt === null ? (
                    <p className="font-display text-[16px] font-bold" style={{ color: debtColor }}>{t("sleep.debt.noData")}</p>
                  ) : debt <= 0 ? (
                    <p className="font-display text-[16px] font-bold" style={{ color: debtColor, textShadow: `0 0 16px ${debtColor}30` }}>{tf("sleep.debt.balance", { target: String(targetHours) })}</p>
                  ) : (
                    <>
                      <p className="font-display text-[18px] font-bold" style={{ color: debtColor, textShadow: `0 0 16px ${debtColor}25` }}>
                        -{Math.floor(debt / 60)}h {debt % 60}min
                      </p>
                      <p className="text-[10px] font-body mt-1" style={{ color: "hsl(218 14% 60%)" }}>
                        {tf("sleep.debt.deficit", { target: String(targetHours) })}
                      </p>
                      <div className="w-full h-1.5 rounded-full overflow-hidden" style={{ background: "hsl(218 14% 18%)" }}>
                        <motion.div className="h-full rounded-full" initial={{ width: 0 }}
                          animate={{ width: `${debtPct}%`, opacity: [0.8, 1, 0.8] }}
                          transition={{ width: { delay: 0.5, duration: 0.8, ease: "easeOut" }, opacity: { duration: 2, repeat: reducedMotion ? 0 : Infinity, ease: "easeInOut" } }}
                          style={{ background: `linear-gradient(90deg, ${debtColor}90, ${debtColor})`, boxShadow: `0 0 8px ${debtColor}40` }} />
                      </div>
                    </>
                  )}
                  <p className="text-[9px] font-body mt-1 mb-2" style={{ color: "hsl(218 14% 50%)" }}>
                    {tf("sleep.debt.window", { nights: String(debtWindow.nights), days: String(debtWindow.days) })}
                  </p>
                </motion.div>
              );
            })()}

            {/* Section E — SLEEP NOTES INSIGHTS */}
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={stagger(4)}
              className="rounded-2xl p-4 mb-5" style={{ background: "linear-gradient(145deg, hsl(220 35% 12%), hsl(225 35% 6%))", border: "1px solid hsl(220 50% 45% / 0.30)" }}>
              {stats.topNotes.length > 0 ? (
                <div className="space-y-2">
                  {stats.topNotes.map(ni => {
                    const worse = ni.avgWith < ni.avgWithout;
                    return (
                      <div key={ni.note} className="px-3 py-2 rounded-xl" style={{ background: "hsl(220 50% 12% / 0.40)", border: `1px solid ${worse ? "hsl(38 92% 55% / 0.25)" : "hsl(220 50% 30% / 0.15)"}` }}>
                        <p className="text-[11px] font-body" style={{ color: worse ? "hsl(38 92% 65%)" : "hsl(218 14% 65%)" }}>
                          {tf("sleep.noteInsight", { note: ni.note.replace("_", " "), with: String(ni.avgWith), without: String(ni.avgWithout) })}
                        </p>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-[11px] font-body text-center py-2" style={{ color: "hsl(218 14% 60%)" }}>
                  {t("sleep.noteInsightNeeded")}
                </p>
              )}
            </motion.div>

            {/* ⛔ REMOVIDO: o "Mirror Insight" do rodapé.
                Estava atrás de `{false && …}` desde a auditoria de 23/04, com
                o bilhete "fake Mirror Insight — re-wire with real Padrão B", e
                o texto dele era uma frase FIXA no locale. Promovê-lo ao topo
                colocaria uma frase inventada na primeira dobra — o oposto do
                que este lote conserta. A leitura REAL (edge sleep-react) subiu
                para o topo como <MirrorSleepCard />, e é ela que aparece. */}

          </motion.div>
        )}

        {/* ═══════════════ TAB: INSIGHTS ═══════════════ */}
        {activeTab === "insights" && (
          <motion.div key="tab-insights" className="relative z-10" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} transition={{ duration: 0.25 }}>

            {/* Section A — A FONTE DO DADO, LIDA DE user_connections.
                Era um bloco com Apple Watch / Garmin / Oura / Whoop os quatro
                em "Coming Soon", enquanto a aba Log ao lado mostrava noites
                etiquetadas APPLE HEALTH. Ver o cabeçalho de WearableSources. */}
            <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={stagger(0)}>
              <WearableSources />
            </motion.div>

            {/* Sections B e C — RECUPERAÇÃO e PRONTIDÃO, por CÁLCULO.
                Diziam "Available with wearable" para quem TEM wearable: o que
                faltava era função, não sensor. Agora saem de HRV + FC em
                repouso DERIVADA (mínimo da janela noturna) + sono, e se
                apresentam como ESTIMATIVA — o rótulo vem no tipo do dado. */}
            <EstimatedScoreCard
              score={recovery}
              icon={Heart}
              titleKey="sleep.insights.recovery"
              subtitleKey="sleep.scores.recoveryWhat"
              accent="hsl(142 60% 50%)"
              delay={0.05}
            />
            <EstimatedScoreCard
              score={readiness}
              icon={Zap}
              titleKey="sleep.insights.readiness"
              subtitleKey="sleep.scores.readinessWhat"
              accent={ACCENT}
              delay={0.12}
            />

            {/* ── TODA TENDÊNCIA MORA AQUI ───────────────────────────────
                Médias de 7 e 30 dias, consistência de horário e tendência de
                HRV. Cada uma devolve o DENOMINADOR na própria linha: as
                funções puras de lib/sleep/trends.ts entregam `days` e
                `nights` junto do valor de propósito. */}
            <SleepTrendSummary nights={trendNights} now={trendNow} />
            <ScheduleConsistencyCard nights={trendNights} now={trendNow} />
            <HrvTrendCard points={scoreInput.hrv} now={scoreInput.now} />

            {/* ── EVOLUÇÃO — veio da aba Log, onde empurrava tudo para fora
                da tela. ⚠️ A JANELA AGORA ESTÁ ESCRITA no cabeçalho: havia
                gráfico terminando em 29/jun ao lado de outro indo até 17/ago,
                nenhum dos dois dizendo de que período era. */}
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.15 }}
              className="rounded-2xl p-4 mb-5" style={{ background: "linear-gradient(145deg, hsl(220 35% 12%), hsl(225 35% 6%))", border: "1px solid hsl(220 50% 45% / 0.30)" }}>
              <p className="text-[9px] font-display tracking-[0.18em] uppercase" style={{ color: "hsl(220 70% 70% / 0.70)" }}>
                {t("sleep.analytics.title")}
              </p>
              <p className="text-[9px] font-body mb-3 mt-0.5" style={{ color: "hsl(218 14% 50%)" }}>
                {tf("sleep.analytics.window", { days: "365" })}
              </p>
              {historyLogs !== null && <AnalyticsDashboard
                results={sleepAnalytics}
                granularity={sleepGran}
                onGranularityChange={setSleepGran}
                loading={historyLoading && historyLogs === null}
                emptyKey="sleep.analytics.empty"
              />}
            </motion.div>

            {/* Section D — SLEEP CYCLES (zeroed) */}
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={stagger(3)}
              className="rounded-2xl p-4 mb-4 relative overflow-hidden" style={{ background: "linear-gradient(145deg, hsl(220 35% 12%), hsl(225 35% 6%))", border: "1px solid hsl(220 50% 40% / 0.15)", filter: "saturate(0.5)" }}>
              <p className="text-[9px] font-display tracking-[0.18em] uppercase mb-3" style={{ color: "hsl(220 70% 65% / 0.35)" }}>{t("sleep.insights.sleepStages")}</p>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  {[{ label: t("sleep.light"), color: "hsl(218 14% 55%)" }, { label: t("sleep.deep"), color: "hsl(220 55% 55%)" }, { label: t("sleep.rem"), color: "hsl(265 60% 65%)" }].map(p => (
                    <div key={p.label} className="flex items-center gap-1">
                      <div className="w-1.5 h-1.5 rounded-full opacity-40" style={{ background: p.color }} />
                      <span className="text-[8px] font-display" style={{ color: "hsl(218 14% 50%)" }}>{p.label}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ overflow: "hidden" }}>
                <motion.svg width="100%" height="80" viewBox="0 0 320 80" preserveAspectRatio="none"
                  animate={{ x: [0, -10, 0] }}
                  transition={{ duration: 6, repeat: reducedMotion ? 0 : Infinity, ease: "easeInOut" }}>
                  <defs>
                    <linearGradient id="waveGradDim" x1="0%" y1="0%" x2="0%" y2="100%"><stop offset="0%" stopColor="hsl(265 60% 50%)" stopOpacity="0.12" /><stop offset="100%" stopColor="hsl(220 50% 30%)" stopOpacity="0.02" /></linearGradient>
                    <linearGradient id="waveGradDim2" x1="0%" y1="0%" x2="0%" y2="100%"><stop offset="0%" stopColor="hsl(220 55% 45%)" stopOpacity="0.08" /><stop offset="100%" stopColor="hsl(220 50% 30%)" stopOpacity="0.01" /></linearGradient>
                  </defs>
                  <path d={`M 0 45 ${Array.from({ length: 33 }, (_, i) => `L ${i * 10} ${42 + Math.sin(i * 0.4) * 5}`).join(" ")} L 320 80 L 0 80 Z`} fill="url(#waveGradDim)" />
                  <path d={`M 0 50 ${Array.from({ length: 33 }, (_, i) => `L ${i * 10} ${48 + Math.cos(i * 0.35) * 4}`).join(" ")} L 320 80 L 0 80 Z`} fill="url(#waveGradDim2)" />
                  <path d={`M 0 42 ${Array.from({ length: 33 }, (_, i) => `Q ${i * 10 + 5} ${42 + Math.sin(i * 0.4) * 5} ${(i + 1) * 10} ${42 + Math.sin((i + 1) * 0.4) * 5}`).join(" ")}`}
                    fill="none" stroke="hsl(265 60% 60% / 0.2)" strokeWidth="1" />
                </motion.svg>
              </div>
              <div className="flex gap-2 mt-3">
                {[{ label: t("sleep.light"), color: "hsl(218 14% 55%)" }, { label: t("sleep.deep"), color: "hsl(220 55% 55%)" }, { label: t("sleep.rem"), color: "hsl(265 60% 65%)" }].map(s => (
                  <div key={s.label} className="flex-1 rounded-xl p-2 text-center opacity-40" style={{ background: `${s.color}08`, border: `1px solid ${s.color}15` }}>
                    <p className="font-display text-[14px] font-bold" style={{ color: s.color }}>--</p>
                    <p className="text-[8px] font-body" style={{ color: "hsl(218 14% 50%)" }}>{s.label}</p>
                  </div>
                ))}
              </div>
              <p className="text-[9px] font-body text-center mt-3" style={{ color: "hsl(218 14% 50%)" }}>{t("sleep.insights.availableWithWearable")}</p>
            </motion.div>

            {/* Section E — FUTURE METRICS */}
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={stagger(4)}
              className="rounded-2xl p-4 mb-2" style={{ background: "linear-gradient(145deg, hsl(220 35% 12%), hsl(225 35% 6%))", border: "1px solid hsl(220 50% 40% / 0.12)" }}>
              <div className="space-y-3">
                {/* A lista mora em futureMetrics.ts, não inline: aqui dentro
                    ela prometia HRV "quando disponível" com a tendência de HRV
                    renderizando NESTA MESMA tela (heartRateVariability está em
                    HEALTH_READ_TYPES). O pin wearableTruth mantém promessa e
                    leitura disjuntas; só o ícone é decisão desta página. */}
                {SLEEP_FUTURE_METRICS.map((fm, mi) => {
                  const m = { key: fm.labelKey, icon: FUTURE_METRIC_ICON[fm.labelKey] ?? Activity };
                  return (
                  <motion.div key={m.key} className="flex items-center gap-3"
                    initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 + mi * 0.08 }}>
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "hsl(220 40% 12%)", border: "1px solid hsl(220 50% 20% / 0.25)" }}>
                      <Lock size={10} style={{ color: `${ACCENT}35` }} />
                    </div>
                    <div className="flex-1">
                      <p className="font-display text-[12px] font-medium" style={{ color: "hsl(218 14% 62%)" }}>{t(m.key)}</p>
                    </div>
                    <m.icon size={14} style={{ color: "hsl(218 14% 30%)" }} />
                  </motion.div>
                  );
                })}
              </div>
              <p className="text-[9px] font-body text-center mt-3" style={{ color: "hsl(218 14% 50%)" }}>{t("sleep.insights.futureMetrics")}</p>
            </motion.div>

          </motion.div>
        )}
      </AnimatePresence>

      {/* ═══════════════ SETTINGS MODAL ═══════════════ */}
      <AnimatePresence>
        {showSettings && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-black/70" onClick={() => { if (!isSaving) setShowSettings(false); }} />
            <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", damping: 28, stiffness: 320 }}
              className="fixed bottom-0 left-0 right-0 z-[60] rounded-t-3xl px-4 pt-5 pb-10 flex flex-col overflow-hidden" style={{ background: "linear-gradient(180deg, hsl(220 35% 9%), hsl(222 38% 7%))", border: "1px solid hsl(220 50% 35% / 0.25)", borderBottom: "none", maxWidth: "480px", margin: "0 auto", maxHeight: "85vh" }}>
              <div className="w-10 h-1 rounded-full bg-white/15 mx-auto mb-5 shrink-0" />
              <div className="flex items-center justify-between mb-5 shrink-0">
                <p className="font-display text-[16px] font-bold text-white">{t("sleep.settings")}</p>
                <button aria-label={t("common.close")} disabled={isSaving} onClick={() => { if (!isSaving) setShowSettings(false); }} className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: "hsl(218 14% 18%)", border: "1px solid hsl(218 14% 25%)" }}>
                  <X size={12} style={{ color: "hsl(218 14% 60%)" }} />
                </button>
              </div>
              <fieldset disabled={isSaving} className="overflow-y-auto flex-1 space-y-4 pb-4" style={{ WebkitOverflowScrolling: "touch" } as any}>
                {/* Target Hours */}
                <div className="p-4 rounded-2xl" style={{ background: "linear-gradient(145deg, hsl(220 40% 9%), hsl(225 35% 6%))", border: "1px solid hsl(220 50% 35% / 0.30)" }}>
                  <div className="flex items-center gap-2 mb-3"><Target size={13} style={{ color: "hsl(220 70% 65%)" }} /><p className="font-display text-[12px] font-semibold text-white">{t("sleep.targetHours")}</p></div>
                  <div className="flex items-center gap-1.5">
                    {["6", "7", "7.5", "8", "9"].map(h => (
                      <button key={h} aria-pressed={settingsTarget === h} onClick={() => { setSettingsTarget(h); }}
                        className="flex-1 py-2 rounded-xl font-display text-[11px] font-bold transition-all"
                        style={{ background: settingsTarget === h ? "linear-gradient(135deg, hsl(220 70% 45%), hsl(220 70% 38%))" : "hsl(220 40% 12%)", color: settingsTarget === h ? "white" : "hsl(218 14% 50%)", border: `1px solid ${settingsTarget === h ? "hsl(220 70% 50%)" : "hsl(220 50% 20% / 0.30)"}`, boxShadow: settingsTarget === h ? "0 0 12px hsl(220 70% 45% / 0.30)" : "none" }}>
                        {h}h
                      </button>
                    ))}
                  </div>
                </div>
                {/* Bedtime Reminder */}
                <div className="p-4 rounded-2xl" style={{ background: "linear-gradient(145deg, hsl(220 40% 9%), hsl(225 35% 6%))", border: "1px solid hsl(220 50% 35% / 0.30)" }}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2 min-w-0"><Bell size={13} className="shrink-0" style={{ color: "hsl(220 70% 65%)" }} /><p className="font-display text-[12px] font-semibold text-white truncate">{t("sleep.bedtimeReminder")}</p></div>
                    <button role="switch" aria-checked={smartAlarm} aria-label={t("sleep.bedtimeReminder")} onClick={() => { setSmartAlarm(!smartAlarm); }}
                      className="w-10 h-6 rounded-full transition-all relative shrink-0" style={{ background: smartAlarm ? "hsl(220 70% 45%)" : "hsl(218 14% 20%)", boxShadow: smartAlarm ? "0 0 10px hsl(220 70% 45% / 0.35)" : "none" }}>
                      <div className="w-4 h-4 rounded-full bg-white absolute top-1 transition-all" style={{ left: smartAlarm ? "22px" : "2px" }} />
                    </button>
                  </div>
                  {smartAlarm && (
                    <input aria-label={t("sleep.bedtimeReminder")} type="time" value={reminderTime} onChange={e => { setReminderTime(e.target.value); }}
                      className="w-full min-w-0 rounded-xl px-3 py-2 font-display text-[13px] font-semibold text-white focus:outline-none" style={{ background: "hsl(220 50% 12%)", border: "1px solid hsl(220 50% 30% / 0.30)", colorScheme: "dark", maxWidth: "100%", boxSizing: "border-box" }} />
                  )}
                </div>
                {/* Wake-up Reminder — LIVE: persisted to user_preferences on
                    Save and consumed by the sleep-wakeup-message cron
                    (OneSignal push at the configured local time). Same pref
                    is also editable from Settings → Notifications. */}
                <div className="p-4 rounded-2xl" style={{ background: "linear-gradient(145deg, hsl(220 40% 9%), hsl(225 35% 6%))", border: "1px solid hsl(220 50% 35% / 0.30)" }}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2 min-w-0"><Bell size={13} className="shrink-0" style={{ color: "hsl(220 70% 65%)" }} /><p className="font-display text-[12px] font-semibold text-white truncate">{t("sleep.wakeupReminder")}</p></div>
                    <button role="switch" aria-checked={wakeupReminder} aria-label={t("sleep.wakeupReminder")} onClick={() => { setWakeupReminder(!wakeupReminder); }}
                      className="w-10 h-6 rounded-full transition-all relative shrink-0" style={{ background: wakeupReminder ? "hsl(220 70% 45%)" : "hsl(218 14% 20%)", boxShadow: wakeupReminder ? "0 0 10px hsl(220 70% 45% / 0.35)" : "none" }}>
                      <div className="w-4 h-4 rounded-full bg-white absolute top-1 transition-all" style={{ left: wakeupReminder ? "22px" : "2px" }} />
                    </button>
                  </div>
                  {wakeupReminder && (
                    <input aria-label={t("sleep.wakeupReminder")} type="time" value={wakeupReminderTime} onChange={e => { setWakeupReminderTime(e.target.value); }}
                      className="w-full min-w-0 rounded-xl px-3 py-2 font-display text-[13px] font-semibold text-white focus:outline-none" style={{ background: "hsl(220 50% 12%)", border: "1px solid hsl(220 50% 30% / 0.30)", colorScheme: "dark", maxWidth: "100%", boxSizing: "border-box" }} />
                  )}
                </div>
                {/* A FONTE DO DADO — o MESMO componente da aba Insights.
                    Este bloco tinha a segunda cópia da mentira: os mesmos
                    quatro aparelhos em "Coming Soon", incluindo o que já
                    estava conectado. Duas cópias divergem na primeira edição;
                    uma só não tem como. */}
                <WearableSources compact />
              </fieldset>
              <button disabled={isSaving} onClick={handleSettingsSave}
                className="w-full py-3 rounded-xl font-display text-[13px] font-semibold tracking-wide transition-all active:scale-[0.97] mt-4 shrink-0 relative overflow-hidden"
                style={{ background: "linear-gradient(135deg, hsl(220 70% 52%), hsl(220 70% 42%))", color: "#fff", boxShadow: "0 4px 24px hsl(220 70% 50% / 0.40)" }}>
                {isSaving ? t("agenda.saving") : t("sleep.saveSettings")}
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showSheet && (
          <SleepLogSheet
            open={showSheet}
            initialTimes={sheetTimes}
            onClose={() => setShowSheet(false)}
            onSave={handleSheetSave}
            targetHours={parseFloat(String(targetHours)) || 8}
          />
        )}
      </AnimatePresence>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("sleep.edit.deleteConfirmTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("sleep.edit.deleteConfirmDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>
              {t("common.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t("sleep.edit.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Per-night edit sheet (history) — times + quality; duration/score
          recomputed; source and sleep_date preserved */}
      <AgendaBottomSheet
        dismissDisabled={savingRecord}
        open={editRecord !== null}
        onClose={() => { if (!savingRecord) setEditRecord(null); }}
        title={t("sleep.history.editTitle")}
        footer={
          <button
            onClick={saveRecordEdit}
            disabled={savingRecord || !editRecBed || !editRecWake || !editRecQuality}
            className="w-full py-3 rounded-xl font-display text-[14px] font-semibold disabled:opacity-40"
            style={{ background: "linear-gradient(135deg, hsl(220 70% 52%), hsl(220 70% 42%))", color: "#fff" }}
          >
            {savingRecord ? t("agenda.saving") : t("common.save")}
          </button>
        }
      >
        <div className="space-y-4 pb-2">
          <div className="flex gap-3">
            <label className="block flex-1">
              <span className="text-[11px] text-muted-foreground">{t("sleep.bedtime")}</span>
              <input
                type="time"
                disabled={savingRecord} value={editRecBed}
                onChange={(e) => setEditRecBed(e.target.value)}
                className="w-full mt-1 px-3 py-2 rounded-lg text-sm text-foreground outline-none"
                style={{ background: "hsl(222 18% 12%)", border: "1px solid hsl(222 18% 22%)" }}
              />
            </label>
            <label className="block flex-1">
              <span className="text-[11px] text-muted-foreground">{t("sleep.wakeUp")}</span>
              <input
                type="time"
                disabled={savingRecord} value={editRecWake}
                onChange={(e) => setEditRecWake(e.target.value)}
                className="w-full mt-1 px-3 py-2 rounded-lg text-sm text-foreground outline-none"
                style={{ background: "hsl(222 18% 12%)", border: "1px solid hsl(222 18% 22%)" }}
              />
            </label>
          </div>
          <div>
            <span className="text-[11px] text-muted-foreground">{t("sleep.sheet.qualityQuestion")}</span>
            <div className="flex gap-2 mt-2">
              {QUALITY_OPTIONS.map((q) => {
                const Icon = q.icon;
                const active = editRecQuality === q.value;
                return (
                  <button
                    key={q.value}
                    onClick={() => setEditRecQuality(q.value)}
                    className="flex-1 h-11 rounded-xl flex items-center justify-center transition-all active:scale-95"
                    style={{
                      background: active ? `${q.color}22` : "hsl(222 18% 12%)",
                      border: `1px solid ${active ? q.color : "hsl(222 18% 22%)"}`,
                    }}
                    aria-label={t(q.key)} aria-pressed={active} disabled={savingRecord}
                  >
                    <Icon size={16} style={{ color: active ? q.color : "hsl(218 14% 55%)" }} />
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </AgendaBottomSheet>

      {userId && <SleepPendingActionLauncher ownerId={userId} busy={isSaving||!!actionPrefill} onMirrorResume={locator=>navigate('/mirror',{state:{sleepActionRecovery:locator}})} onConfirmed={onActionConfirmed} />}
      <SleepActionModal open={!!actionPrefill} prefill={actionPrefill} onClose={()=>setActionPrefill(null)} onCreated={()=>undefined} onConfirmed={onActionConfirmed} />
    </MobileLayout>
  );
};

export default Sleep;
